class AnimationPlayer {
    meshType;

    constructor() {
        this.meshType = {
            VOLUME: "volume",
            TREE: "tree",
            GROUP: "group",
            MODEL: "model"
        };
        this.animationPlayList = new Array(MAX_RUNNING_ANIMATIONS + 1);
        this.animationIdMap = {};
    }

    _getAnimationId(meshType, branchId) {
        if (this.animationIdMap[meshType + branchId]) {
            return this.animationIdMap[meshType + branchId];
        }

        return this._getFreeAnimationId();
    }

    _getFreeAnimationId() {
        for (let i = 1; i <= MAX_RUNNING_ANIMATIONS; i++) {
            if (!this.animationPlayList[i]) {
                return i;
            }
        }
    }

    _getAnimation(meshType, branchId) {
        const animationId = this.animationIdMap[meshType + branchId];
        if (animationId) {
            return this.animationPlayList[animationId];
        }
    }

    getAllAnimations() {
        const animations = [];
        for (const id in this.animationIdMap) {
            const animationId = this.animationIdMap[id];
            if (animationId && this.animationPlayList[animationId]) {
                animations.push(this.animationPlayList[animationId]);
            }
        }

        return animations;
    }

    addToPlayList(animationInfo) {
        this.animationPlayList[animationInfo.animationId] = animationInfo;
        this.animationIdMap[animationInfo.meshType + animationInfo.branchId] = animationInfo.animationId;
    }

    removeFromPlayList(animationInfo) {
        const animationId = this.animationIdMap[animationInfo.meshType + animationInfo.branchId];
        if (animationId) {
            this.animationPlayList[animationId] = null;
            delete this.animationIdMap[animationInfo.meshType + animationInfo.branchId];
        }
    }

    getVolumes(meshType, branchId) {
        switch (meshType) {
            case this.meshType.VOLUME:
                return [branchId];
            case this.meshType.TREE:
                const node = MeshHierarchyViewFeatureData.getDataFromBranchID(branchId);
                return node?.getLeaves();
            case this.meshType.GROUP:
                const group = GroupData.getGroupById(branchId) || GroupData.getGroupByName(branchId);
                return group.getValidVolumeBranchIds();
            case this.meshType.MODEL:
                return modelManager.getBranchIds(branchId);
            default:
                throw "Invalid mesh type";
        }
    }

    getPivotPoint(animation, volumeList) {
        if (animation.pivot) {
            return new THREE.Vector3(...animation.pivot);
        }

        const boundingBox = new THREE.Box3();
        volumeList.forEach(branchId => {
            const volume = modelManager.getVolume(branchId);
            boundingBox.union(volume.bounding_box);
        })

        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        return center;
    }

    play(animation, meshType, branchId, loop) {
        try {
            let animationInfo = this._getAnimation(meshType, branchId);
            if (animationInfo) {
                animationInfo.loop = loop;
                Transformation.playAnimation(animationInfo);
            } else {

                const volumeList = this.getVolumes(meshType, branchId);
                const modelId = modelManager.getVolume(volumeList[0]).modelId;

                const animationId = this._getAnimationId(meshType, branchId);
                animationInfo = {
                    animationId: animationId,
                    state: {
                        isPaused: false,
                    },
                    animation: animation,
                    loop: loop,
                    meshType: meshType,
                    branchId: branchId,
                    modelId: modelId,
                    volumeList: volumeList,
                    pivot: this.getPivotPoint(animation, volumeList),
                    tween: null
                };
                this.addToPlayList(animationInfo);

                Transformation.playAnimation(animationInfo);
            }

            setIorStatus(wv.effectController.iorEnabled, true);
        } catch (error) {
            console.error(error);
        }
    }

    pause(meshType, branchId) {
        const animationInfo = this._getAnimation(meshType, branchId);
        if (animationInfo && animationInfo.tween) {
            animationInfo.state.isPaused = true;
            animationInfo.tween.stop();
        }
    }

    stop(meshType, branchId) {
        const animationInfo = this._getAnimation(meshType, branchId);
        if (animationInfo) {
            if (animationInfo.tween) {
                animationInfo.tween.stop();
            } else if (animationInfo.state.isPaused) {
                Transformation.finishAnimation(animationInfo);
            }
        }
    }

    stopAnimations(animationArray) {
        for (const animationInfo of animationArray) {
            this.stop(animationInfo.meshType, animationInfo.branchId);
            console.warn("Animation named " + animationInfo.animation.name + " has been stopped forcefully.");
        }
    }

    isClickedOnAnimatedMesh(mouseEvent) {
        if (isLoadSessionRunning() || !isAnyFeatureActive()) {
            return false;
        }

        const intersectedBranchId = mouseEvent.pickingData.volume?.branchID;
        if (!intersectedBranchId) {
            return false;
        }

        const allAnimations = this.getAllAnimations();
        for (let animation of allAnimations) {
            if (animation.volumeList?.includes(intersectedBranchId)) {
                this.stop(animation.meshType, animation.branchId);
                return true;
            }
        }

        return false;
    }

    getAnimationOfChildBranches(meshType, branchId) {
        const meshAnimations = [];
        const volumeSet = new Set(this.getVolumes(meshType, branchId));
        meshAnimations.push(...this.getAllAnimations().filter(animation => animation.volumeList.some(volume => volumeSet.has(volume))));

        return meshAnimations;
    }

    stopConflictingMeshAnimations(meshType, branchId, keepPausedAnimations = false, animationResourceId) {
        const animationInfo = this._getAnimation(meshType, branchId);
        if (animationInfo) {
            if (!keepPausedAnimations || !animationInfo.state.isPaused || animationInfo.animation.id !== animationResourceId) {
                this.stopAnimations([animationInfo]);
            }
        } else {
            const meshChildAnimations = this.getAnimationOfChildBranches(meshType, branchId);
            this.stopAnimations(meshChildAnimations);
        }

        // for the animation of move(model/volume/group/branch)
        this.stopConflictingMoveAnimations(meshType, branchId);

    }

    stopConflictingMoveAnimations(meshType, branchId) {
        const meshVolumes = this.getVolumes(meshType, branchId) || [];
        const modelId = getModelIdFromMeshName(meshVolumes[0]);
        if (modelId) {
            CADModelTransformationAnimationManager.completeAndRemoveCurrentAnimationOfThisModel(modelId);
        }

        for (let branchId of meshVolumes) {
            CADModelTransformationAnimationManager.completeAndRemoveCurrentAnimationOfThisVolume(branchId);
        }
    }

    stopAllAnimationsOfThisModel(modelId) {
        const allAnimations = this.getAllAnimations();
        allAnimations.forEach(animationInfo => {
            if (animationInfo.modelId === modelId) {
                this.stop(animationInfo.meshType, animationInfo.branchId);
                console.warn("Animation named " + animation.animation.name + " has been stopped forcefully.");
            }
        });
    }

    stopAllAnimations() {
        const allAnimations = this.getAllAnimations();
        this.stopAnimations(allAnimations);
    }
}

export default new AnimationPlayer();
